import type { HTMLAttributes, ReactNode } from 'react'

import { cn } from '../cn'

export type Tone = 'neutral' | 'recovered' | 'atrisk' | 'lost' | 'info' | 'accent' | 'brand'

const TONE: Record<Tone, string> = {
  neutral: 'bg-paper text-muted border-line',
  recovered: 'bg-recovered-bg text-recovered border-transparent',
  atrisk: 'bg-atrisk-bg text-atrisk border-transparent',
  lost: 'bg-lost-bg text-lost border-transparent',
  info: 'bg-info-bg text-info border-transparent',
  accent: 'bg-accent/15 text-gold-deep border-transparent',
  brand: 'bg-brand text-brand-fg border-transparent',
}

export interface PillProps extends HTMLAttributes<HTMLSpanElement> {
  readonly tone?: Tone
  readonly icon?: ReactNode
  /** Small leading dot — for status lists. */
  readonly dot?: boolean
}

/** The prototype's `.pill`: uppercase, 11 px, bold. Colour is never the only carrier — text is. */
export function Pill({ tone = 'neutral', icon, dot, className, children, ...rest }: PillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-[3px] text-[11px] font-bold tracking-[0.02em] uppercase whitespace-nowrap',
        TONE[tone],
        className,
      )}
      {...rest}
    >
      {dot && <span aria-hidden className="size-1.5 rounded-full bg-current" />}
      {icon}
      {children}
    </span>
  )
}

/**
 * Tone for the backend's state vocabularies (contracts/enums.ts). Unknown values fall back to
 * neutral so a new backend state renders rather than crashes.
 */
const STATE_TONES: Readonly<Record<string, Tone>> = {
  // appointments
  scheduled: 'neutral',
  confirmation_pending: 'atrisk',
  confirmed: 'recovered',
  at_risk: 'atrisk',
  cancelled: 'lost',
  no_show: 'lost',
  rescheduled: 'info',
  completed: 'recovered',
  open_slot: 'info',
  held: 'atrisk',
  filled: 'recovered',
  expired: 'neutral',
  // recovery overall / tracks / offers
  detected: 'info',
  outreach_active: 'atrisk',
  recovered: 'recovered',
  partially_recovered: 'atrisk',
  closed_unrecovered: 'lost',
  manual_review: 'lost',
  not_started: 'neutral',
  declined: 'lost',
  pending: 'neutral',
  offered: 'info',
  accepted: 'recovered',
  // messaging delivery
  queued: 'neutral',
  sent: 'info',
  delivered: 'recovered',
  failed: 'lost',
  // waitlist / dueback / integrations / users / notifications
  active: 'recovered',
  inactive: 'neutral',
  fulfilled: 'recovered',
  not_contacted: 'atrisk',
  contacted: 'info',
  disconnected: 'neutral',
  connected: 'recovered',
  syncing: 'info',
  error: 'lost',
  invited: 'info',
  disabled: 'lost',
  critical: 'lost',
  warn: 'atrisk',
}

export function toneForState(state: string): Tone {
  return STATE_TONES[state] ?? 'neutral'
}

export function humanizeState(state: string): string {
  return state.replace(/_/g, ' ')
}

export function StatePill({
  state,
  className,
}: {
  readonly state: string
  readonly className?: string
}) {
  return (
    <Pill tone={toneForState(state)} dot className={className}>
      {humanizeState(state)}
    </Pill>
  )
}

export function CountBadge({
  count,
  className,
}: {
  readonly count: number
  readonly className?: string
}) {
  if (count <= 0) return null
  return (
    <span
      className={cn(
        'inline-flex min-w-[18px] items-center justify-center rounded-full bg-lost px-1.5 text-[10px] font-bold text-white',
        className,
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  )
}
